import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Send, Loader2, RotateCcw, ArrowDown } from 'lucide-react';

/* ────────────────────────────────────────────
   Translations
   ──────────────────────────────────────────── */
const translations = {
  en: {
    greeting: (name) =>
      `Hey ${name}! 👋\n\nI'm Carlos Mario, your request assistant.\n\nTell me — **what do you need help with today?** Describe your request in your own words and I'll take it from there.`,
    newRequest: 'New Request',
    inputPlaceholder: 'Describe what you need…',
    savedSuccess: () =>
      `✅ **Request submitted!**\n\nYour request has been added to the planner and the team will review it.\n\nNeed anything else? Just type away.`,
    savedError: '❌ Failed to save. Please try again.',
    chatError: 'Sorry, something went wrong. Try again.',
    typing: 'Carlos is thinking…'
  },
  pt: {
    greeting: (name) =>
      `Olá ${name}! 👋\n\nEu sou o Carlos Mario, seu assistente de solicitações.\n\nMe conta — **no que posso te ajudar hoje?** Descreva sua necessidade com suas próprias palavras.`,
    newRequest: 'Nova Solicitação',
    inputPlaceholder: 'Descreva o que você precisa…',
    savedSuccess: () =>
      `✅ **Solicitação enviada!**\n\nSua solicitação foi adicionada ao planner e a equipe vai analisar.\n\nPrecisa de mais alguma coisa? Basta digitar.`,
    savedError: '❌ Falha ao salvar. Tente novamente.',
    chatError: 'Desculpe, algo deu errado. Tente de novo.',
    typing: 'Carlos está pensando…'
  },
  es: {
    greeting: (name) =>
      `¡Hola ${name}! 👋\n\nSoy Carlos Mario, tu asistente de solicitudes.\n\nCuéntame — **¿en qué puedo ayudarte hoy?** Describe lo que necesitas con tus propias palabras.`,
    newRequest: 'Nueva Solicitud',
    inputPlaceholder: 'Describe lo que necesitas…',
    savedSuccess: () =>
      `✅ **¡Solicitud enviada!**\n\nTu solicitud ha sido agregada al planner y el equipo la revisará.\n\n¿Necesitas algo más? Solo escribe.`,
    savedError: '❌ Error al guardar. Inténtalo de nuevo.',
    chatError: 'Lo siento, algo salió mal. Inténtalo de nuevo.',
    typing: 'Carlos está pensando…'
  }
};

/* ────────────────────────────────────────────
   Simple markdown-ish renderer for bold text
   ──────────────────────────────────────────── */
function renderMessageContent(text) {
  if (!text) return null;
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return React.createElement('strong', { key: i }, part.slice(2, -2));
    }
    return part;
  });
}

/* ════════════════════════════════════════════
   CARLOS MARIO — Conversational Request Agent
   ════════════════════════════════════════════ */
function CarlosMario({ user, workspaceId }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [gatheredInfo, setGatheredInfo] = useState({});
  const [language, setLanguage] = useState('en');
  const [initialized, setInitialized] = useState(false);
  const [showScrollBtn, setShowScrollBtn] = useState(false);
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const inputRef = useRef(null);

  const t = translations[language] || translations.en;

  /* ── Data loading ── */
  const loadPreferences = useCallback(async () => {
    try {
      const response = await axios.get('/api/user/preferences');
      setLanguage(response.data.language || 'en');
    } catch (err) {
      console.error('Failed to load preferences:', err);
    }
  }, []);

  const [langLoaded, setLangLoaded] = useState(false);

  useEffect(() => {
    loadPreferences().then(() => setLangLoaded(true)).catch(() => setLangLoaded(true));
  }, [loadPreferences]);

  /* ── Auto-greet once language preference is loaded ── */
  useEffect(() => {
    if (!initialized && langLoaded && user?.name) {
      const lang = translations[language] || translations.en;
      const firstName = user.name.split(' ')[0];
      setMessages([{ role: 'assistant', content: lang.greeting(firstName) }]);
      setInitialized(true);
    }
  }, [user, language, initialized, langLoaded]);

  /* ── Scroll behaviour ── */
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  const handleScroll = useCallback(() => {
    const el = chatContainerRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    setShowScrollBtn(!atBottom);
  }, []);

  /* ── Send message ── */
  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = { role: 'user', content: input.trim() };
    setMessages(prev => [...prev, userMessage]);
    const currentInput = input.trim();
    setInput('');
    setLoading(true);

    try {
      const conversationHistory = messages.map(m => ({ role: m.role, content: m.content }));
      const response = await axios.post('/api/agents/carlos-chat', {
        message: currentInput,
        conversationHistory,
        gatheredInfo
      });

      const botReply = { role: 'assistant', content: response.data.message };
      setMessages(prev => [...prev, botReply]);

      if (response.data.gatheredInfo) {
        setGatheredInfo(response.data.gatheredInfo);
      }
      if (response.data.isComplete && response.data.savedItem) {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: t.savedSuccess()
        }]);
      }
    } catch (err) {
      console.error('Chat error:', err);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: err.response?.data?.error || t.chatError
      }]);
    } finally {
      setLoading(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  /* ── Reset ── */
  const handleReset = () => {
    setMessages([]);
    setGatheredInfo({});
    setInitialized(false);
  };

  useEffect(() => {
    handleReset();
  }, [workspaceId]);

  /* ═══════════════════════════════════════════
     RENDER — Full-screen conversational UI
     ═══════════════════════════════════════════ */
  return (
    <div className="cm-root" data-fullwidth="true" style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      background: '#f7f7f8',
      position: 'relative',
      overflow: 'hidden'
    }}>

      {/* ── Top Bar ── */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 16px',
        background: '#ffffff',
        borderBottom: '1px solid #e5e5e5',
        flexShrink: 0,
        minHeight: '56px',
        zIndex: 10
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {/* Agent avatar */}
          <div style={{
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            background: '#1c1c1e',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '16px',
            flexShrink: 0
          }}>
            ✦
          </div>
          <div>
            <div style={{
              fontWeight: 700,
              fontSize: '15px',
              color: '#1c1c1e',
              lineHeight: 1.2,
              fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif"
            }}>
              Carlos Mario
            </div>
            <div style={{
              fontSize: '12px',
              color: loading ? '#0d9488' : '#9ca3af',
              fontWeight: 500,
              lineHeight: 1.2,
              fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif"
            }}>
              {loading ? t.typing : 'Request Assistant'}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {messages.length > 1 && (
            <button
              onClick={handleReset}
              style={{
                padding: '7px 14px',
                background: 'transparent',
                border: '1px solid #e5e5e5',
                borderRadius: '20px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                color: '#6b7280',
                fontSize: '13px',
                fontWeight: 500,
                fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
                whiteSpace: 'nowrap'
              }}
            >
              <RotateCcw size={13} /> {t.newRequest}
            </button>
          )}
        </div>
      </div>

      {/* ── Messages ── */}
      <div
        ref={chatContainerRef}
        onScroll={handleScroll}
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          WebkitOverflowScrolling: 'touch'
        }}
      >
        <div style={{ maxWidth: '1100px', width: '100%', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {messages.map((msg, idx) => (
            <div
              key={idx}
              style={{
                display: 'flex',
                justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                animationName: 'cmFadeSlideIn',
                animationDuration: '0.3s',
                animationTimingFunction: 'ease-out',
                animationFillMode: 'both',
                animationDelay: idx === messages.length - 1 ? '0.05s' : '0s'
              }}
            >
              <div style={{
                maxWidth: 'min(85%, 760px)',
                padding: '12px 16px',
                borderRadius: msg.role === 'user'
                  ? '20px 20px 4px 20px'
                  : '20px 20px 20px 4px',
                background: msg.role === 'user'
                  ? '#0d9488'
                  : '#ffffff',
                color: msg.role === 'user' ? '#ffffff' : '#1c1c1e',
                boxShadow: msg.role === 'user'
                  ? 'none'
                  : '0 1px 3px rgba(0,0,0,0.06)',
                whiteSpace: 'pre-wrap',
                lineHeight: 1.55,
                fontSize: '15px',
                fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
                wordBreak: 'break-word'
              }}>
                {renderMessageContent(msg.content)}
              </div>
            </div>
          ))}

          {/* Typing indicator */}
          {loading && (
            <div style={{
              display: 'flex',
              justifyContent: 'flex-start',
              animationName: 'cmFadeSlideIn',
              animationDuration: '0.2s',
              animationTimingFunction: 'ease-out',
              animationFillMode: 'both'
            }}>
              <div style={{
                padding: '14px 20px',
                borderRadius: '20px 20px 20px 4px',
                background: '#ffffff',
                boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                display: 'flex',
                gap: '5px',
                alignItems: 'center'
              }}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#b0b0b0', animationName: 'cmDotBounce', animationDuration: '1.2s', animationIterationCount: 'infinite', animationDelay: '0s' }} />
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#b0b0b0', animationName: 'cmDotBounce', animationDuration: '1.2s', animationIterationCount: 'infinite', animationDelay: '0.2s' }} />
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#b0b0b0', animationName: 'cmDotBounce', animationDuration: '1.2s', animationIterationCount: 'infinite', animationDelay: '0.4s' }} />
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Scroll to bottom button */}
      {showScrollBtn && (
        <button
          onClick={scrollToBottom}
          style={{
            position: 'absolute',
            bottom: '86px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            background: '#ffffff',
            border: '1px solid #e5e5e5',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 5,
            color: '#6b7280'
          }}
        >
          <ArrowDown size={16} />
        </button>
      )}

      {/* ── Input bar ── */}
      <div style={{
        padding: '12px 16px',
        paddingBottom: 'max(12px, env(safe-area-inset-bottom, 12px))',
        borderTop: '1px solid #e5e5e5',
        background: '#ffffff',
        flexShrink: 0
      }}>
        <form
          onSubmit={handleSend}
          style={{
            display: 'flex',
            gap: '10px',
            maxWidth: '1100px',
            margin: '0 auto',
            alignItems: 'flex-end'
          }}
        >
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={t.inputPlaceholder}
            disabled={loading}
            style={{
              flex: 1,
              padding: '13px 18px',
              border: '1px solid #e5e5e5',
              borderRadius: '24px',
              fontSize: '15px',
              outline: 'none',
              background: '#f7f7f8',
              color: '#1c1c1e',
              fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
              transition: 'border-color 0.2s, background 0.2s',
              WebkitAppearance: 'none',
              minHeight: '48px',
              boxSizing: 'border-box'
            }}
            onFocus={(e) => { e.target.style.borderColor = '#0d9488'; e.target.style.background = '#fff'; }}
            onBlur={(e) => { e.target.style.borderColor = '#e5e5e5'; e.target.style.background = '#f7f7f8'; }}
          />
          <button
            type="submit"
            disabled={!input.trim() || loading}
            style={{
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              background: input.trim() && !loading ? '#0d9488' : '#e5e5e5',
              color: input.trim() && !loading ? '#ffffff' : '#9ca3af',
              border: 'none',
              cursor: input.trim() && !loading ? 'pointer' : 'default',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              transition: 'background 0.2s, color 0.2s, transform 0.1s'
            }}
          >
            {loading
              ? React.createElement(Loader2, { size: 20, style: { animation: 'cmSpin 1s linear infinite' } })
              : React.createElement(Send, { size: 18, style: { marginLeft: '2px' } })
            }
          </button>
        </form>
      </div>

      {/* ── Animations ── */}
      <style>{`
        @keyframes cmFadeSlideIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes cmDotBounce {
          0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
          30% { transform: translateY(-4px); opacity: 1; }
        }
        @keyframes cmSpin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap');
      `}</style>
    </div>
  );
}

export default CarlosMario;
